import React, { useState, useEffect, useRef } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import api from '../utils/api';
import { S, NOTE_CATS, NOTE_COLORS, formatDate, daysUntil } from '../utils/styles';

function TabBtn({ label, active, onClick, badge }) {
  return (
    <div onClick={onClick} style={{ padding:'9px 18px', fontSize:13, cursor:'pointer',
      color:active?'#3b82f6':'#6b7280', borderBottom:active?'2px solid #3b82f6':'2px solid transparent',
      fontWeight:active?500:400, display:'flex', gap:6, alignItems:'center', whiteSpace:'nowrap' }}>
      {label}{badge ? <span style={{ background:'#ef4444', color:'#fff', borderRadius:20, padding:'1px 6px', fontSize:10 }}>{badge}</span> : null}
    </div>
  );
}

// Line Items Tab — with inline edit/delete (fix #6)
function LineItemsTab({ order, items, realItems, totalVal, totalOrdered, totalShipped, reload }) {
  const [editingId, setEditingId] = useState(null);
  const [editRow, setEditRow] = useState({});

  const startEdit = item => { setEditingId(item.id); setEditRow({ size: item.size, qty_ordered: item.qty_ordered, price_per_unit: item.price_per_unit, line_notes: item.line_notes||'' }); };
  const cancelEdit = () => { setEditingId(null); setEditRow({}); };

  const saveEdit = async (itemId, orderId) => {
    try {
      await api.put(`/orders/${orderId}/items/${itemId}`, editRow);
      setEditingId(null); reload();
    } catch(e) { alert(e.response?.data?.error || 'Error saving'); }
  };

  const deleteRow = async (itemId, orderId) => {
    if (!window.confirm('Remove this line item?')) return;
    try { await api.delete(`/orders/${orderId}/items/${itemId}`); reload(); }
    catch(e) { alert(e.response?.data?.error || 'Error deleting'); }
  };

  const inp = { ...S.input, fontSize:12, padding:'4px 7px' };

  return (
    <div>
      <div style={{ fontSize:12, color:'#9ca3af', marginBottom:10 }}>Click ✎ on any row to edit. Changes save immediately.</div>
      <table style={{ width:'100%', borderCollapse:'collapse' }}>
        <thead><tr>{['#','Product','Size / desc','Qty ordered','Shipped','Price/pc','Line value','Notes',''].map(h=><th key={h} style={S.th}>{h}</th>)}</tr></thead>
        <tbody>
          {items.map((item,i) => {
            if (item.section_header && !item.size) return (
              <tr key={item.id}><td colSpan={9} style={{ ...S.td, background:'#f9fafb', fontWeight:600, fontSize:12, color:'#374151', padding:'10px 14px' }}>{item.section_header}</td></tr>
            );
            const lineVal = (item.qty_ordered||0)*(item.price_per_unit||0);
            const pct = item.qty_ordered > 0 ? Math.round((item.qty_shipped/item.qty_ordered)*100) : 0;
            const isEditing = editingId === item.id;
            if (isEditing) {
              return (
                <tr key={item.id} style={{ borderBottom:'1px solid #bfdbfe', background:'#eff6ff' }}>
                  <td style={{ ...S.td, color:'#9ca3af', fontSize:11 }}>{i+1}</td>
                  <td style={{ ...S.td, color:'#3b82f6', fontWeight:600 }}>{item.product_code||'—'}</td>
                  <td style={{ padding:'4px 6px' }}><input value={editRow.size} onChange={e=>setEditRow(r=>({...r,size:e.target.value}))} style={inp} /></td>
                  <td style={{ padding:'4px 6px' }}><input type="number" value={editRow.qty_ordered} onChange={e=>setEditRow(r=>({...r,qty_ordered:e.target.value}))} style={{ ...inp, width:80 }} /></td>
                  <td style={{ ...S.td, color:pct>=100?'#10b981':pct>0?'#f59e0b':'#9ca3af' }}>{(item.qty_shipped||0).toLocaleString()}</td>
                  <td style={{ padding:'4px 6px' }}><input type="number" value={editRow.price_per_unit} onChange={e=>setEditRow(r=>({...r,price_per_unit:e.target.value}))} step="0.001" style={{ ...inp, width:90 }} /></td>
                  <td style={S.td}>{order.currency} {((parseFloat(editRow.qty_ordered)||0)*(parseFloat(editRow.price_per_unit)||0)).toLocaleString(undefined,{minimumFractionDigits:2,maximumFractionDigits:2})}</td>
                  <td style={{ padding:'4px 6px' }}><input value={editRow.line_notes} onChange={e=>setEditRow(r=>({...r,line_notes:e.target.value}))} style={inp} /></td>
                  <td style={{ padding:'4px 6px' }}>
                    <div style={{ display:'flex', gap:4 }}>
                      <button onClick={() => saveEdit(item.id, order.id)} style={{ ...S.btnSm, background:'#3b82f6', color:'#fff', fontSize:11 }}>✓ Save</button>
                      <button onClick={cancelEdit} style={{ ...S.btnSm, background:'#f3f4f6', fontSize:11 }}>✕</button>
                    </div>
                  </td>
                </tr>
              );
            }
            return (
              <tr key={item.id} style={{ borderBottom:'1px solid #f3f4f6' }}>
                <td style={{ ...S.td, color:'#9ca3af', fontSize:11 }}>{i+1}</td>
                <td style={{ ...S.td, color:'#3b82f6', fontWeight:600 }}>{item.product_code||'—'}</td>
                <td style={S.td}>{item.size}</td>
                <td style={S.td}>{(item.qty_ordered||0).toLocaleString()}</td>
                <td style={{ ...S.td, color:pct>=100?'#10b981':pct>0?'#f59e0b':'#9ca3af' }}>{(item.qty_shipped||0).toLocaleString()} ({pct}%)</td>
                <td style={S.td}>{order.currency} {(item.price_per_unit||0).toFixed(4)}</td>
                <td style={{ ...S.td, fontWeight:500 }}>{order.currency} {lineVal.toLocaleString(undefined,{minimumFractionDigits:2,maximumFractionDigits:2})}</td>
                <td style={{ ...S.td, color:'#6b7280', fontSize:12 }}>{item.line_notes||'—'}</td>
                <td style={{ padding:'4px 8px' }}>
                  <div style={{ display:'flex', gap:4 }}>
                    <button onClick={() => startEdit(item)} title="Edit row" style={{ background:'none', border:'none', color:'#9ca3af', cursor:'pointer', fontSize:13 }}>✎</button>
                    <button onClick={() => deleteRow(item.id, order.id)} title="Delete row" style={{ background:'none', border:'none', color:'#fca5a5', cursor:'pointer', fontSize:16 }}>×</button>
                  </div>
                </td>
              </tr>
            );
          })}
          <tr style={{ background:'#f9fafb', fontWeight:600 }}>
            <td colSpan={3} style={S.td}>Total</td>
            <td style={S.td}>{totalOrdered.toLocaleString()}</td>
            <td style={S.td}>{totalShipped.toLocaleString()}</td>
            <td colSpan={2} style={{ ...S.td, color:'#3b82f6' }}>{order.currency} {totalVal.toLocaleString(undefined,{minimumFractionDigits:2,maximumFractionDigits:2})}</td>
            <td colSpan={2} style={S.td}></td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}

// Quick Add Material Modal — used inside BOM tab
function QuickAddMaterialModal({ onSave, onClose }) {
  const CATS = ['Coil','Rubber','Fastener','Box','Chemical','Other'];
  const UNITS = ['kg','tons','pcs','m','rolls','boxes','liters','sheets'];
  const [form, setForm] = useState({ name:'', category:'Coil', spec1:'', spec2:'', spec3:'', unit:'kg', consumption_mode:'manual', reorder_level:'0', current_stock:'0' });
  const [saving, setSaving] = useState(false);
  const f = k => v => setForm(p => ({...p,[k]:v}));
  const save = async () => {
    if (!form.name) return;
    setSaving(true);
    try { const {data} = await api.post('/inventory/materials', form); onSave(data); }
    catch(e) { alert(e.response?.data?.error || 'Error saving material'); }
    finally { setSaving(false); }
  };
  return (
    <div style={{ ...S.overlay, zIndex:1200 }}>
      <div style={{ ...S.modal, width:520 }}>
        <div style={{ fontSize:15, fontWeight:600, marginBottom:16 }}>Add new material to inventory</div>
        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12, marginBottom:14 }}>
          <div style={{ gridColumn:'span 2' }}><label style={S.label}>Material name *</label><input value={form.name} onChange={e => f('name')(e.target.value)} style={S.input} placeholder="e.g. MS Coil" /></div>
          <div><label style={S.label}>Category</label><select value={form.category} onChange={e => f('category')(e.target.value)} style={S.input}>{CATS.map(c=><option key={c}>{c}</option>)}</select></div>
          <div><label style={S.label}>Unit</label><select value={form.unit} onChange={e => f('unit')(e.target.value)} style={S.input}>{UNITS.map(u=><option key={u}>{u}</option>)}</select></div>
          <div><label style={S.label}>Spec 1 (thickness/size)</label><input value={form.spec1} onChange={e => f('spec1')(e.target.value)} style={S.input} placeholder="e.g. 1.5mm" /></div>
          <div><label style={S.label}>Spec 2</label><input value={form.spec2} onChange={e => f('spec2')(e.target.value)} style={S.input} placeholder="e.g. 19.5mm" /></div>
          <div><label style={S.label}>Consumption mode</label>
            <select value={form.consumption_mode} onChange={e => f('consumption_mode')(e.target.value)} style={S.input}>
              <option value="manual">Manual (coils, rubber)</option>
              <option value="auto">Auto on dispatch (nuts, screws, boxes)</option>
            </select>
          </div>
          <div><label style={S.label}>Opening stock</label><input type="number" value={form.current_stock} onChange={e => f('current_stock')(e.target.value)} style={S.input} /></div>
        </div>
        <div style={{ display:'flex', gap:10 }}>
          <button onClick={save} disabled={saving||!form.name} style={S.btnPrimary}>{saving?'Saving...':'Save & use material'}</button>
          <button onClick={onClose} style={S.btnSecondary}>Cancel</button>
        </div>
      </div>
    </div>
  );
}

// BOM Tab
function BOMTab({ order, items }) {
  const [selected, setSelected] = useState(items[0]?.id || null);
  const [entries, setEntries] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [suggestion, setSuggestion] = useState(null);
  const [summary, setSummary] = useState([]);
  const [saving, setSaving] = useState(false);
  const [showAddMaterial, setShowAddMaterial] = useState(false);

  useEffect(() => { loadMaterials(); loadSummary(); }, []);
  useEffect(() => { if (selected) { loadBOM(selected); loadSuggestion(selected); } }, [selected]);

  const loadMaterials = () => api.get('/inventory/materials').then(r => setMaterials(r.data));
  const loadBOM = async id => {
    const { data } = await api.get(`/orders/${order.id}/items/${id}/bom`);
    setEntries(data.length > 0 ? data : [{ raw_material_id:'', qty_required:'', unit:'', cost_per_unit:'', notes:'' }]);
  };
  const loadSuggestion = async id => {
    const { data } = await api.get(`/orders/${order.id}/items/${id}/bom/suggest`);
    setSuggestion(data);
    // Also build within-order carry-forward: find other items in same order with BOMs already saved
    // that share the same product_code prefix
    const selectedItem = items.find(i => i.id === id);
    if (!selectedItem) return;
    const sameProductCode = selectedItem.product_code;
    // look for saved BOMs on other items with same product_code in this order
    const othersWithBOM = items.filter(i =>
      i.id !== id && i.product_code === sameProductCode
    );
    for (const other of othersWithBOM) {
      try {
        const { data: otherBOM } = await api.get(`/orders/${order.id}/items/${other.id}/bom`);
        if (otherBOM.length > 0 && (!data.entries || data.entries.length === 0)) {
          setSuggestion({ source: 'same_order', label: `Same order — ${other.size}`, entries: otherBOM });
          break;
        }
      } catch(e) {}
    }
  };
  const loadSummary = async () => {
    const { data } = await api.get(`/orders/${order.id}/bom-summary`);
    setSummary(data);
  };

  const addEntry = () => setEntries(e => [...e, { raw_material_id:'', qty_required:'', unit:'', cost_per_unit:'', notes:'' }]);
  const removeEntry = i => setEntries(e => e.filter((_,idx) => idx !== i));
  const updateEntry = (i,k,v) => setEntries(e => e.map((en,idx) => idx===i ? {...en,[k]:v} : en));

  const applySuggestion = () => {
    if (!suggestion?.entries?.length) return;
    setEntries(suggestion.entries.map(e => ({ raw_material_id: e.material_id || e.raw_material_id, qty_required: e.qty_required, unit: e.unit || '', cost_per_unit: e.cost_per_unit || '', notes: e.notes || '' })));
  };

  const saveBOM = async () => {
    setSaving(true);
    try {
      await api.post(`/orders/${order.id}/items/${selected}/bom`, { entries: entries.filter(e => e.raw_material_id && e.qty_required) });
      loadSummary(); loadMaterials();
      alert('BOM saved and stock reserved.');
    } catch(e) { alert(e.response?.data?.error || 'Error saving BOM'); }
    finally { setSaving(false); }
  };

  const onMaterialAdded = (newMat) => {
    setMaterials(m => [...m, newMat]);
    setShowAddMaterial(false);
    // Auto-select the new material in the last empty entry
    setEntries(e => {
      const lastEmpty = e.findIndex(en => !en.raw_material_id);
      if (lastEmpty >= 0) {
        const next = [...e];
        next[lastEmpty] = { ...next[lastEmpty], raw_material_id: String(newMat.id), unit: newMat.unit };
        return next;
      }
      return [...e, { raw_material_id: String(newMat.id), qty_required:'', unit: newMat.unit, cost_per_unit:'', notes:'' }];
    });
  };

  const selectedItem = items.find(i => i.id === selected);

  return (
    <div style={{ display:'grid', gridTemplateColumns:'200px 1fr', gap:20 }}>
      {showAddMaterial && <QuickAddMaterialModal onSave={onMaterialAdded} onClose={() => setShowAddMaterial(false)} />}
      <div>
        <div style={{ fontSize:11, fontWeight:600, color:'#9ca3af', textTransform:'uppercase', letterSpacing:.7, marginBottom:8 }}>Line items</div>
        {items.map(item => (
          <div key={item.id} onClick={() => setSelected(item.id)} style={{ padding:'9px 11px', borderRadius:7, cursor:'pointer', marginBottom:4,
            background:selected===item.id?'#eff6ff':'#f9fafb', color:selected===item.id?'#3b82f6':'#374151', fontSize:13, border:selected===item.id?'1px solid #bfdbfe':'1px solid #e5e7eb' }}>
            <div style={{ fontWeight:500 }}>{item.product_code || item.product_name || 'Item'}</div>
            <div style={{ fontSize:11, color:'#9ca3af' }}>{item.size} · {(item.qty_ordered||0).toLocaleString()} pcs</div>
          </div>
        ))}
      </div>

      <div>
        {selectedItem && (
          <>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:14 }}>
              <div style={{ fontWeight:600, fontSize:15 }}>BOM — {selectedItem.product_code || 'Item'} {selectedItem.size}</div>
              <div style={{ display:'flex', gap:8 }}>
                {suggestion?.entries?.length > 0 && (
                  <button onClick={applySuggestion} style={{ ...S.btnSecondary, fontSize:12 }}>
                    Use previous BOM ({suggestion.source === 'same_order' ? suggestion.label : suggestion.source === 'same_customer' ? 'same customer' : 'same product'})
                  </button>
                )}
                <button onClick={() => { setShowAddMaterial(true); }} style={{ ...S.btnSecondary, fontSize:12 }}>+ New material</button>
                <button onClick={addEntry} style={{ ...S.btnSecondary, fontSize:12 }}>+ Add row</button>
                <button onClick={saveBOM} disabled={saving} style={{ ...S.btnPrimary, fontSize:12 }}>{saving ? 'Saving...' : 'Save & reserve'}</button>
              </div>
            </div>

            <div style={{ overflowX:'auto', marginBottom:16 }}>
              <table style={{ width:'100%', borderCollapse:'collapse', minWidth:600 }}>
                <thead><tr>{['Material','Qty required','Unit','Cost/unit (₹)','Notes',''].map(h => <th key={h} style={S.th}>{h}</th>)}</tr></thead>
                <tbody>
                  {entries.map((e,i) => (
                    <tr key={i} style={{ borderBottom:'1px solid #f3f4f6' }}>
                      <td style={{ padding:'5px 6px' }}>
                        <select value={e.raw_material_id} onChange={ev => {
                          updateEntry(i,'raw_material_id',ev.target.value);
                          const mat = materials.find(m => m.id == ev.target.value);
                          if (mat) updateEntry(i,'unit',mat.unit);
                        }} style={{ ...S.input, fontSize:12, minWidth:200 }}>
                          <option value="">Select material...</option>
                          {materials.map(m => <option key={m.id} value={m.id}>{m.name}{m.spec1?' · '+m.spec1:''}{m.spec2?' · '+m.spec2:''}{m.spec3?' · '+m.spec3:''}</option>)}
                        </select>
                      </td>
                      <td style={{ padding:'5px 6px' }}><input type="number" value={e.qty_required} onChange={ev => updateEntry(i,'qty_required',ev.target.value)} style={{ ...S.input, fontSize:12, width:90 }} placeholder="0" /></td>
                      <td style={{ padding:'5px 6px' }}><input value={e.unit} onChange={ev => updateEntry(i,'unit',ev.target.value)} style={{ ...S.input, fontSize:12, width:70 }} placeholder="kg/pcs" /></td>
                      <td style={{ padding:'5px 6px' }}><input type="number" value={e.cost_per_unit} onChange={ev => updateEntry(i,'cost_per_unit',ev.target.value)} style={{ ...S.input, fontSize:12, width:90 }} placeholder="0" /></td>
                      <td style={{ padding:'5px 6px' }}><input value={e.notes} onChange={ev => updateEntry(i,'notes',ev.target.value)} style={{ ...S.input, fontSize:12 }} placeholder="optional" /></td>
                      <td style={{ padding:'5px 6px' }}><button onClick={() => removeEntry(i)} style={{ background:'none', border:'none', color:'#ef4444', cursor:'pointer', fontSize:18 }}>×</button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {summary.length > 0 && (
              <div style={{ background:'#f9fafb', borderRadius:8, padding:14 }}>
                <div style={{ fontWeight:600, fontSize:13, marginBottom:10 }}>Inventory check — entire order</div>
                <table style={{ width:'100%', borderCollapse:'collapse' }}>
                  <thead><tr>{['Material','Required','Free stock','To order'].map(h => <th key={h} style={S.th}>{h}</th>)}</tr></thead>
                  <tbody>
                    {summary.map(m => (
                      <tr key={m.id} style={{ borderBottom:'1px solid #e5e7eb' }}>
                        <td style={{ ...S.td, fontWeight:500 }}>{m.name}{m.spec1?' · '+m.spec1:''}{m.spec2?' · '+m.spec2:''}{m.spec3?' · '+m.spec3:''}</td>
                        <td style={S.td}>{m.total_required} {m.unit}</td>
                        <td style={{ ...S.td, color: m.free_stock <= 0 ? '#ef4444' : m.free_stock < m.total_required ? '#f59e0b' : '#10b981', fontWeight:500 }}>{m.free_stock}</td>
                        <td style={{ ...S.td, color: m.to_order > 0 ? '#ef4444':'#10b981', fontWeight:500 }}>{m.to_order > 0 ? `⚠ ${m.to_order}` : '✓ Covered'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

// Production Tracker — HORIZONTAL (stages as columns)
function StagesTab({ order, items }) {
  const [selected, setSelected] = useState(items[0]?.id || null);
  const [stages, setStages] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [newStageName, setNewStageName] = useState('');
  const [dragOverId, setDragOverId] = useState(null);

  const handleDragStart = (e, id) => e.dataTransfer.setData('stageId', String(id));
  const handleDrop = async (e, targetId) => {
    e.preventDefault();
    const draggedId = parseInt(e.dataTransfer.getData('stageId'));
    if (draggedId === targetId) return;
    setDragOverId(null);
    const reordered = [...stages];
    const fromIdx = reordered.findIndex(s => s.id === draggedId);
    const toIdx = reordered.findIndex(s => s.id === targetId);
    const [moved] = reordered.splice(fromIdx, 1);
    reordered.splice(toIdx, 0, moved);
    const withOrder = reordered.map((s, i) => ({ ...s, col_order: i }));
    setStages(withOrder);
    try { await api.post(`/orders/stages/reorder`, { stages: withOrder.map(s => ({ id: s.id, col_order: s.col_order })) }); } catch(e) {}
  };

  useEffect(() => { api.get('/inventory/job-vendors').then(r => setVendors(r.data)); }, []);
  useEffect(() => { if (selected) loadStages(selected); }, [selected]);

  const loadStages = async id => {
    const { data } = await api.get(`/orders/${order.id}/items/${id}/stages`);
    setStages(data);
  };

  const addStage = async () => {
    if (!newStageName.trim()) return;
    await api.post(`/orders/${order.id}/items/${selected}/stages`, { stage_name: newStageName, col_order: stages.length });
    setNewStageName(''); loadStages(selected);
  };

  const updateStage = async (stageId, field, value) => {
    const stage = stages.find(s => s.id === stageId);
    if (!stage) return;
    try {
      await api.put(`/orders/stages/${stageId}`, { ...stage, [field]: value });
      setStages(ss => ss.map(s => s.id === stageId ? {...s, [field]:value} : s));
    } catch(e) {}
  };

  const deleteStage = async id => {
    if (!window.confirm('Remove this stage?')) return;
    await api.delete(`/orders/stages/${id}`); loadStages(selected);
  };

  const renameStage = async (id, name) => {
    const newName = window.prompt('Rename stage:', name);
    if (!newName || newName === name) return;
    await api.put(`/orders/stages/${id}/rename`, { stage_name: newName });
    loadStages(selected);
  };

  const selectedItem = items.find(i => i.id === selected);
  const totalOrdered = selectedItem?.qty_ordered || 0;

  const QUICK_STAGES = ['Cut nut','Cut bottom','Bend nut','Bend bottom','Spot weld','Tap','EG plating','Rubber assy','Screw assy','Boxing'];
  const availableQuickStages = QUICK_STAGES.filter(s => !stages.some(st => st.stage_name === s));

  return (
    <div style={{ display:'grid', gridTemplateColumns:'200px 1fr', gap:20 }}>
      <div>
        <div style={{ fontSize:11, fontWeight:600, color:'#9ca3af', textTransform:'uppercase', letterSpacing:.7, marginBottom:8 }}>Line items</div>
        {items.map(item => (
          <div key={item.id} onClick={() => setSelected(item.id)} style={{ padding:'9px 11px', borderRadius:7, cursor:'pointer', marginBottom:4,
            background:selected===item.id?'#eff6ff':'#f9fafb', color:selected===item.id?'#3b82f6':'#374151', fontSize:13, border:selected===item.id?'1px solid #bfdbfe':'1px solid #e5e7eb' }}>
            <div style={{ fontWeight:500 }}>{item.product_code || item.product_name || 'Item'}</div>
            <div style={{ fontSize:11, color:'#9ca3af' }}>{item.size} · {(item.qty_ordered||0).toLocaleString()} pcs</div>
          </div>
        ))}
      </div>

      <div>
        {selectedItem && (
          <>
            <div style={{ fontWeight:600, fontSize:15, marginBottom:14 }}>
              Production — {selectedItem.product_code || 'Item'} {selectedItem.size} ({totalOrdered.toLocaleString()} pcs ordered)
            </div>

            {/* HORIZONTAL STAGE GRID — fix #12: proper overflow-x */}
            <div style={{ overflowX:'auto', marginBottom:16, border:'1px solid #e5e7eb', borderRadius:8, maxWidth:'100%' }}>
              <table style={{ borderCollapse:'collapse', minWidth: Math.max(600, stages.length * 160 + 200) }}>
                <thead>
                  <tr style={{ background:'#f9fafb' }}>
                    <th style={{ ...S.th, minWidth:120, position:'sticky', left:0, background:'#f9fafb', zIndex:2 }}>Field</th>
                    {stages.map(s => (
                      <th key={s.id} style={{ ...S.th, minWidth:150, textAlign:'center', cursor:'grab',
                        background: dragOverId===s.id ? '#dbeafe' : '#f9fafb',
                        borderLeft: dragOverId===s.id ? '2px solid #3b82f6' : 'none' }}
                        draggable
                        onDragStart={e => handleDragStart(e, s.id)}
                        onDragOver={e => { e.preventDefault(); setDragOverId(s.id); }}
                        onDragLeave={() => setDragOverId(null)}
                        onDrop={e => handleDrop(e, s.id)}
                      >
                        <div style={{ display:'flex', alignItems:'center', justifyContent:'center', gap:4 }}>
                          <span>{s.stage_name}</span>
                          <button onClick={() => renameStage(s.id, s.stage_name)} style={{ background:'none', border:'none', color:'#9ca3af', cursor:'pointer', fontSize:11, padding:0 }}>✎</button>
                          <button onClick={() => deleteStage(s.id)} style={{ background:'none', border:'none', color:'#fca5a5', cursor:'pointer', fontSize:13, padding:0 }}>×</button>
                        </div>
                        {s.updated_at && <div style={{ fontSize:10, color:'#9ca3af', fontWeight:400 }}>{s.updated_at?.split('T')[0]}</div>}
                      </th>
                    ))}
                    {stages.length === 0 && <th style={{ ...S.th, color:'#9ca3af', fontStyle:'italic' }}>Add stages below →</th>}
                  </tr>
                </thead>
                <tbody>
                  {/* Done row */}
                  <tr style={{ borderBottom:'1px solid #f3f4f6' }}>
                    <td style={{ ...S.td, fontWeight:500, background:'#fafafa', position:'sticky', left:0, zIndex:1 }}>Done (pcs)</td>
                    {stages.map(s => {
                      const pct = totalOrdered > 0 ? Math.round((s.qty_done/totalOrdered)*100) : 0;
                      return (
                        <td key={s.id} style={{ padding:'8px 10px', textAlign:'center' }}>
                          <input type="number" value={s.qty_done} min={0} max={totalOrdered}
                            onChange={e => updateStage(s.id,'qty_done',parseInt(e.target.value)||0)}
                            style={{ ...S.input, width:80, textAlign:'center', fontSize:13 }} />
                          <div style={{ marginTop:4 }}>
                            <div style={{ width:80, height:4, background:'#e5e7eb', borderRadius:2, overflow:'hidden', margin:'0 auto' }}>
                              <div style={{ width:`${pct}%`, height:'100%', background:pct>=100?'#10b981':'#3b82f6' }} />
                            </div>
                            <div style={{ fontSize:10, color:'#9ca3af', marginTop:2 }}>{pct}%</div>
                          </div>
                        </td>
                      );
                    })}
                    {stages.length === 0 && <td style={{ ...S.td, color:'#9ca3af', textAlign:'center' }}>—</td>}
                  </tr>
                  {/* With vendor row */}
                  <tr style={{ borderBottom:'1px solid #f3f4f6' }}>
                    <td style={{ ...S.td, fontWeight:500, background:'#fafafa', position:'sticky', left:0, zIndex:1 }}>With vendor</td>
                    {stages.map(s => (
                      <td key={s.id} style={{ padding:'8px 10px', textAlign:'center' }}>
                        <input type="number" value={s.qty_with_vendor} min={0}
                          onChange={e => updateStage(s.id,'qty_with_vendor',parseInt(e.target.value)||0)}
                          style={{ ...S.input, width:80, textAlign:'center', fontSize:13 }} />
                      </td>
                    ))}
                    {stages.length === 0 && <td style={{ ...S.td, color:'#9ca3af', textAlign:'center' }}>—</td>}
                  </tr>
                  {/* Vendor row */}
                  <tr>
                    <td style={{ ...S.td, fontWeight:500, background:'#fafafa', position:'sticky', left:0, zIndex:1 }}>Vendor</td>
                    {stages.map(s => (
                      <td key={s.id} style={{ padding:'8px 10px', textAlign:'center' }}>
                        <select value={s.vendor_id||''} onChange={e => updateStage(s.id,'vendor_id',e.target.value||null)}
                          style={{ ...S.input, fontSize:12, width:120 }}>
                          <option value="">None</option>
                          {vendors.map(v => <option key={v.id} value={v.id}>{v.name}</option>)}
                        </select>
                        {vendors.length === 0 && <div style={{ fontSize:10, color:'#9ca3af', marginTop:3 }}>
                          <a href="/vendors" style={{ color:'#3b82f6', textDecoration:'none' }}>+ Add vendors →</a>
                        </div>}
                      </td>
                    ))}
                    {stages.length === 0 && <td style={{ ...S.td, color:'#9ca3af', textAlign:'center' }}>—</td>}
                  </tr>
                  {/* Last updated row */}
                  <tr style={{ background:'#f9fafb' }}>
                    <td style={{ ...S.td, fontWeight:400, fontSize:11, color:'#9ca3af', background:'#f9fafb', position:'sticky', left:0, zIndex:1 }}>Last updated</td>
                    {stages.map(s => {
                      const d = s.updated_at ? new Date(s.updated_at) : null;
                      const daysAgo = d ? Math.floor((Date.now()-d.getTime())/(1000*60*60*24)) : null;
                      const label = daysAgo === null ? '—' : daysAgo === 0 ? 'Today' : daysAgo === 1 ? 'Yesterday' : `${daysAgo}d ago`;
                      return <td key={s.id} style={{ padding:'6px 10px', textAlign:'center', fontSize:11, color:'#9ca3af' }}>{label}</td>;
                    })}
                    {stages.length === 0 && <td style={{ ...S.td, color:'#9ca3af', textAlign:'center' }}>—</td>}
                  </tr>
                </tbody>
              </table>
            </div>

            {/* Add stage */}
            <div style={{ display:'flex', gap:8, alignItems:'center', flexWrap:'wrap' }}>
              <input value={newStageName} onChange={e => setNewStageName(e.target.value)}
                onKeyDown={e => e.key==='Enter'&&addStage()}
                placeholder="Stage name..." style={{ ...S.input, width:220 }} />
              <button onClick={addStage} disabled={!newStageName.trim()} style={S.btnSecondary}>+ Add stage</button>
              {availableQuickStages.length > 0 && <span style={{ fontSize:12, color:'#9ca3af' }}>Quick:</span>}
              {availableQuickStages.map(s => (
                <button key={s} onClick={() => { setNewStageName(s); setTimeout(()=>{ document.querySelector('input[placeholder="Stage name..."]')?.focus(); },50); }}
                  style={{ ...S.btnSm, background:'#f3f4f6', color:'#374151', whiteSpace:'nowrap', fontSize:11 }}>{s}</button>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// Edit Shipment Row — inline edit for dispatch date, containers, notes
function EditShipmentRow({ shipment, orderId, onSaved }) {
  const [date, setDate] = useState(shipment.dispatch_date);
  const [containers, setContainers] = useState(shipment.containers_count || '');
  const [notes, setNotes] = useState(shipment.notes || '');
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/orders/${orderId}/shipments/${shipment.id}`, { dispatch_date: date, notes, containers_count: containers });
      onSaved();
    } catch(e) { alert(e.response?.data?.error || 'Error saving'); setSaving(false); }
  };

  return (
    <tr style={{ background:'#f0f9ff', borderBottom:'1px solid #bae6fd' }}>
      <td style={{ padding:'8px 10px' }}>
        <input type="date" value={date} onChange={e => setDate(e.target.value)} style={{ ...S.input, fontSize:12, width:140 }} />
      </td>
      <td style={{ padding:'8px 10px' }}>
        <input type="number" value={containers} onChange={e => setContainers(e.target.value)} style={{ ...S.input, fontSize:12, width:60 }} placeholder="0" />
      </td>
      <td colSpan={2} style={{ padding:'8px 10px', color:'#6b7280', fontSize:12 }}>Quantities cannot be edited — only date, containers and notes</td>
      <td style={{ padding:'8px 10px' }}>
        <input value={notes} onChange={e => setNotes(e.target.value)} style={{ ...S.input, fontSize:12 }} placeholder="Notes..." />
      </td>
      <td style={{ padding:'8px 10px' }}>
        <button onClick={save} disabled={saving} style={{ ...S.btnSm, background:'#3b82f6', color:'#fff', fontSize:11 }}>{saving ? '...' : '✓ Save'}</button>
      </td>
    </tr>
  );
}

// Dispatch Tab
function DispatchTab({ order, items, shipments, reload }) {
  const [dispatchDate, setDispatchDate] = useState(new Date().toISOString().split('T')[0]);
  const [notes, setNotes] = useState('');
  const [containersCount, setContainersCount] = useState('');
  // Show ALL items that have a size — no production restriction, no qty filter
  const dispatchableItems = items.filter(i => i.size && i.size.trim() !== '');
  const [dispItems, setDispItems] = useState(dispatchableItems.map(i => ({ order_item_id:i.id, qty_shipped:'', pcs_per_carton:'' })));
  const [warning, setWarning] = useState(null);
  const [saving, setSaving] = useState(false);
  // Fix #15: edit dispatch
  const [editingShipmentId, setEditingShipmentId] = useState(null);

  const updateDisp = (id, k, v) => setDispItems(arr => arr.map(i => i.order_item_id === id ? {...i,[k]:v} : i));

  const dispatch = async (confirmed=false) => {
    setSaving(true); setWarning(null);
    try {
      const validItems = dispItems.filter(i => parseInt(i.qty_shipped) > 0);
      if (validItems.length === 0) { alert('Enter qty for at least one item'); setSaving(false); return; }
      await api.post(`/orders/${order.id}/shipments`, { dispatch_date:dispatchDate, notes, containers_count: parseInt(containersCount)||0, items:validItems, confirmed });
      reload(); setDispItems(dispatchableItems.map(i => ({ order_item_id:i.id, qty_shipped:'', pcs_per_carton:'' }))); setNotes(''); setContainersCount('');
    } catch(e) {
      if (e.response?.status === 422 && e.response.data.warning) setWarning(e.response.data);
      else alert(e.response?.data?.error || 'Error dispatching');
    } finally { setSaving(false); }
  };

  const total = dispItems.reduce((s,i) => s + (parseInt(i.qty_shipped)||0), 0);
  const totalVal = dispItems.reduce((s,i) => {
    const item = items.find(it => it.id === i.order_item_id);
    return s + ((parseInt(i.qty_shipped)||0) * (item?.price_per_unit||0));
  }, 0);

  return (
    <div>
      {shipments.length > 0 && (
        <div style={{ marginBottom:20 }}>
          <div style={{ fontWeight:600, fontSize:14, marginBottom:10 }}>Previous shipments</div>
          <table style={{ width:'100%', borderCollapse:'collapse', background:'#fff', borderRadius:8, overflow:'hidden', border:'1px solid #e5e7eb' }}>
            <thead><tr>{['Date','Containers','Pcs','Value','Notes',''].map(h=><th key={h} style={S.th}>{h}</th>)}</tr></thead>
            <tbody>
              {shipments.map(s => (
                <React.Fragment key={s.id}>
                  <tr style={{ borderBottom:'1px solid #f3f4f6' }}>
                    <td style={S.td}>{s.dispatch_date}</td>
                    <td style={{ ...S.td, color:'#6b7280' }}>{s.containers_count||0}</td>
                    <td style={{ ...S.td, fontWeight:500, color:'#10b981' }}>{(s.total_shipped||0).toLocaleString()}</td>
                    <td style={{ ...S.td, color:'#3b82f6', fontWeight:500 }}>{order.currency} {(s.shipment_value||0).toLocaleString(undefined,{minimumFractionDigits:2,maximumFractionDigits:2})}</td>
                    <td style={{ ...S.td, color:'#6b7280', fontSize:12 }}>{s.notes||'—'}</td>
                    <td style={{ padding:'6px 10px' }}>
                      <button onClick={() => setEditingShipmentId(editingShipmentId===s.id ? null : s.id)}
                        style={{ ...S.btnSm, background: editingShipmentId===s.id ? '#1a1f2e' : '#f3f4f6', color: editingShipmentId===s.id ? '#fff' : '#374151', fontSize:11 }}>
                        {editingShipmentId===s.id ? '✕ Cancel' : '✎ Edit'}
                      </button>
                    </td>
                  </tr>
                  {editingShipmentId===s.id && (
                    <EditShipmentRow shipment={s} orderId={order.id} onSaved={() => { setEditingShipmentId(null); reload(); }} />
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div style={{ fontWeight:600, fontSize:14, marginBottom:14 }}>Record new dispatch</div>

      {warning && (
        <div style={{ background:'#fffbeb', border:'1px solid #f59e0b', borderRadius:8, padding:14, marginBottom:14 }}>
          <div style={{ fontWeight:600, color:'#92400e', marginBottom:6 }}>⚠️ Shipping more than ordered qty</div>
          {warning.details.map((d,i) => <div key={i} style={{ fontSize:13, color:'#92400e' }}>{d}</div>)}
          <div style={{ display:'flex', gap:8, marginTop:10 }}>
            <button onClick={() => dispatch(true)} style={{ ...S.btnSm, background:'#f59e0b', color:'#fff' }}>Proceed anyway</button>
            <button onClick={() => setWarning(null)} style={S.btnSm}>Cancel</button>
          </div>
        </div>
      )}

      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:14, marginBottom:14 }}>
        <div><label style={S.label}>Dispatch date *</label><input type="date" value={dispatchDate} onChange={e=>setDispatchDate(e.target.value)} style={S.input} /></div>
        <div><label style={S.label}>No. of containers</label><input type="number" value={containersCount} onChange={e=>setContainersCount(e.target.value)} style={{ ...S.input, width:110 }} placeholder="0" min="0" /></div>
        <div><label style={S.label}>Notes</label><input value={notes} onChange={e=>setNotes(e.target.value)} style={S.input} placeholder="Any notes..." /></div>
      </div>

      <table style={{ width:'100%', borderCollapse:'collapse', background:'#fff', borderRadius:8, border:'1px solid #e5e7eb', marginBottom:14 }}>
        <thead><tr>{['Product','Size','Ordered','Shipped so far','Remaining','Ship now','Pcs/carton → Cartons'].map(h=><th key={h} style={S.th}>{h}</th>)}</tr></thead>
        <tbody>
          {items.map(item => {
            const disp = dispItems.find(d => d.order_item_id === item.id) || {};
            const remaining = (item.qty_ordered||0) - (item.qty_shipped||0);
            const cartons = disp.pcs_per_carton && parseInt(disp.qty_shipped) ? Math.ceil(parseInt(disp.qty_shipped)/parseInt(disp.pcs_per_carton)) : null;
            return (
              <tr key={item.id} style={{ borderBottom:'1px solid #f3f4f6' }}>
                <td style={S.td}>{item.product_code||'—'}</td>
                <td style={S.td}>{item.size}</td>
                <td style={S.td}>{(item.qty_ordered||0).toLocaleString()}</td>
                <td style={{ ...S.td, color:'#6b7280' }}>{(item.qty_shipped||0).toLocaleString()}</td>
                <td style={{ ...S.td, color:remaining<=0?'#10b981':'#374151', fontWeight:500 }}>{remaining>0?remaining.toLocaleString():'✓ Done'}</td>
                <td style={{ padding:'5px 6px' }}><input type="number" value={disp.qty_shipped||''} onChange={e=>updateDisp(item.id,'qty_shipped',e.target.value)} style={{ ...S.input, fontSize:13, width:90 }} placeholder="0" /></td>
                <td style={{ padding:'5px 6px', display:'flex', gap:6, alignItems:'center' }}>
                  <input type="number" value={disp.pcs_per_carton||''} onChange={e=>updateDisp(item.id,'pcs_per_carton',e.target.value)} style={{ ...S.input, fontSize:13, width:70 }} placeholder="pcs/ctn" />
                  {cartons && <span style={{ fontSize:12, color:'#10b981', fontWeight:500 }}>= {cartons} ctns</span>}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>

      {total > 0 && <div style={{ fontSize:13, color:'#6b7280', marginBottom:12 }}>Dispatching: <strong style={{ color:'#111827' }}>{total.toLocaleString()} pcs</strong> · <strong style={{ color:'#3b82f6' }}>{order.currency} {totalVal.toLocaleString(undefined,{minimumFractionDigits:2,maximumFractionDigits:2})}</strong></div>}
      <button onClick={() => dispatch(false)} disabled={saving} style={{ ...S.btnPrimary, padding:'10px 22px' }}>{saving ? 'Recording...' : 'Record dispatch'}</button>
    </div>
  );
}

// Main OrderDetail
export default function OrderDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [tab, setTab] = useState('items');
  const [editingField, setEditingField] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [showNote, setShowNote] = useState(false);
  const [noteForm, setNoteForm] = useState({ category:'General', note:'' });
  const [showDuplicate, setShowDuplicate] = useState(false);
  const [newPi, setNewPi] = useState('');
  const fileRef = useRef();

  useEffect(() => { load(); }, [id]);

  const load = async () => {
    const { data: d } = await api.get(`/orders/${id}`);
    setData(d);
    setEditForm({
      expected_ship_date: d.order?.expected_ship_date||'',
      shipping_terms: d.order?.shipping_terms||'',
      payment_terms: d.order?.payment_terms||'',
      destination_port: d.order?.destination_port||'',
      hs_code: d.order?.hs_code||'73079910',
      special_instructions: d.order?.special_instructions||'',
      customer_po: d.order?.customer_po||''
    });
  };

  const saveHeader = async () => { await api.put(`/orders/${id}`, editForm); load(); setEditingField(null); };

  const addNote = async () => {
    if (!noteForm.note.trim()) return;
    await api.post(`/orders/${id}/notes`, noteForm);
    setNoteForm({ category:'General', note:'' }); setShowNote(false); load();
  };

  const deleteNote = async nid => { if (!window.confirm('Delete note?')) return; await api.delete(`/orders/${id}/notes/${nid}`); load(); };
  const [showCloseModal, setShowCloseModal] = useState(false);
  const closeOrder = () => setShowCloseModal(true);

  const downloadWorkOrder = async () => {
    try {
      const { data } = await api.get(`/orders/${id}/work-order-pdf`, { responseType:'text' });
      const blob = new Blob([data], { type:'text/html' });
      window.open(URL.createObjectURL(blob), '_blank');
    } catch(e) { alert('Error generating work order'); }
  };
  const reopenOrder = async () => { await api.post(`/orders/${id}/reopen`); load(); };

  const deleteOrder = async () => {
    if (!window.confirm(`Permanently delete order ${order?.pi_number}? This cannot be undone. All BOM reservations will be released.`)) return;
    if (!window.confirm('Are you absolutely sure? This will delete all line items, notes, BOM and stage data.')) return;
    try { await api.delete(`/orders/${id}`); nav('/orders'); }
    catch(e) { alert(e.response?.data?.error || 'Error deleting order'); }
  };

  const duplicate = async () => {
    if (!newPi.trim()) return;
    try { const { data: d } = await api.post(`/orders/${id}/duplicate`, { new_pi_number: newPi }); nav(`/orders/${d.id}`); }
    catch(e) { alert(e.response?.data?.error || 'Error'); }
  };

  const uploadDoc = async file => {
    const fd = new FormData(); fd.append('file', file);
    await api.post(`/orders/${id}/documents`, fd, { headers:{'Content-Type':'multipart/form-data'} });
    load();
  };

  const deleteDoc = async did => { if (!window.confirm('Delete document?')) return; await api.delete(`/orders/${id}/documents/${did}`); load(); };

  // ─── CLOSE ORDER MODAL ─────────────────────────────────────────────────────
  const CloseOrderModal = () => {
    const [closeItems, setCloseItems] = useState(
      items.filter(i => i.size && i.size.trim() !== '' && i.qty_ordered > 0).map(i => ({
        order_item_id: i.id, product_code: i.product_code, size: i.size,
        qty_ordered: i.qty_ordered, qty_shipped_so_far: i.qty_shipped || 0,
        final_qty: '', price_per_unit: i.price_per_unit
      }))
    );
    const [extraItems, setExtraItems] = useState([]);
    const [closeDate, setCloseDate] = useState(new Date().toISOString().split('T')[0]);
    const [closeContainers, setCloseContainers] = useState('');
    const [closing, setClosing] = useState(false);

    const updateClose = (idx, v) => setCloseItems(arr => arr.map((it, i) => i === idx ? {...it, final_qty: v} : it));
    const addExtra = () => setExtraItems(e => [...e, { product_code:'', size:'', qty:'', price_per_unit:'' }]);
    const updateExtra = (idx, k, v) => setExtraItems(arr => arr.map((it, i) => i === idx ? {...it,[k]:v} : it));

    const confirmClose = async () => {
      setClosing(true);
      try {
        const shipItems = closeItems.filter(i => parseInt(i.final_qty) > 0).map(i => ({ order_item_id: i.order_item_id, qty_shipped: parseInt(i.final_qty) }));
        const extraShipItems = extraItems.filter(i => i.size && parseInt(i.qty) > 0);
        if (shipItems.length > 0 || extraShipItems.length > 0) {
          await api.post('/orders/'+id+'/close-with-dispatch', { dispatch_date: closeDate, containers_count: parseInt(closeContainers)||0, items: shipItems, extra_items: extraShipItems });
        } else {
          await api.post('/orders/'+id+'/close');
        }
        setShowCloseModal(false); load();
      } catch(e) { alert(e.response?.data?.error || 'Error closing order'); }
      finally { setClosing(false); }
    };

    return (
      <div style={S.overlay}>
        <div style={{ ...S.modal, width:740, maxHeight:'90vh', overflowY:'auto' }}>
          <div style={{ fontSize:16, fontWeight:600, marginBottom:4 }}>Close order — {order?.pi_number}</div>
          <div style={{ fontSize:13, color:'#6b7280', marginBottom:16 }}>Enter final shipped quantities. Ordered quantities will NOT change.</div>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12, marginBottom:14 }}>
            <div>
              <label style={S.label}>Final dispatch date</label>
              <input type="date" value={closeDate} onChange={e => setCloseDate(e.target.value)} style={{ ...S.input, width:'100%' }} />
            </div>
            <div>
              <label style={S.label}>No. of containers</label>
              <input type="number" value={closeContainers} onChange={e => setCloseContainers(e.target.value)} style={S.input} placeholder="0" min="0" />
            </div>
          </div>
          <table style={{ width:'100%', borderCollapse:'collapse', marginBottom:16 }}>
            <thead><tr>
              {['Product','Size','Ordered','Shipped so far','Final shipped qty'].map(h => <th key={h} style={S.th}>{h}</th>)}
            </tr></thead>
            <tbody>
              {closeItems.map((item, i) => (
                <tr key={item.order_item_id} style={{ borderBottom:'1px solid #f3f4f6' }}>
                  <td style={{ ...S.td, color:'#3b82f6', fontWeight:500 }}>{item.product_code||'—'}</td>
                  <td style={S.td}>{item.size}</td>
                  <td style={{ ...S.td, color:'#6b7280' }}>{(item.qty_ordered||0).toLocaleString()}</td>
                  <td style={{ ...S.td, color:'#9ca3af' }}>{(item.qty_shipped_so_far||0).toLocaleString()}</td>
                  <td style={{ padding:'5px 8px' }}>
                    <input type="number" value={item.final_qty}
                      onChange={e => updateClose(i, e.target.value)}
                      placeholder={String(item.qty_ordered - item.qty_shipped_so_far)}
                      style={{ ...S.input, width:100, fontSize:13 }} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {extraItems.length > 0 && (
            <div style={{ marginBottom:14 }}>
              <div style={{ fontWeight:600, fontSize:13, marginBottom:8 }}>Extra items (not in original order)</div>
              <table style={{ width:'100%', borderCollapse:'collapse' }}>
                <thead><tr>{['Product code','Size / Description','Qty shipped','Price/pc',''].map(h=><th key={h} style={S.th}>{h}</th>)}</tr></thead>
                <tbody>
                  {extraItems.map((item, i) => (
                    <tr key={i} style={{ borderBottom:'1px solid #f3f4f6' }}>
                      <td style={{ padding:'5px 6px' }}><input value={item.product_code} onChange={e=>updateExtra(i,'product_code',e.target.value)} style={{ ...S.input, fontSize:12 }} placeholder="e.g. RL-SS316" /></td>
                      <td style={{ padding:'5px 6px' }}><input value={item.size} onChange={e=>updateExtra(i,'size',e.target.value)} style={{ ...S.input, fontSize:12 }} placeholder="e.g. 20-25mm" /></td>
                      <td style={{ padding:'5px 6px' }}><input type="number" value={item.qty} onChange={e=>updateExtra(i,'qty',e.target.value)} style={{ ...S.input, fontSize:12, width:90 }} /></td>
                      <td style={{ padding:'5px 6px' }}><input type="number" value={item.price_per_unit} onChange={e=>updateExtra(i,'price_per_unit',e.target.value)} style={{ ...S.input, fontSize:12, width:90 }} placeholder="0.00" /></td>
                      <td style={{ padding:'5px 6px' }}><button onClick={() => setExtraItems(arr => arr.filter((_,idx)=>idx!==i))} style={{ background:'none', border:'none', color:'#ef4444', cursor:'pointer', fontSize:16 }}>×</button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <button onClick={addExtra} style={{ ...S.btnSecondary, marginBottom:16, fontSize:12 }}>+ Add extra item (shipped outside original order)</button>
          <div style={{ display:'flex', gap:10, paddingTop:14, borderTop:'1px solid #e5e7eb' }}>
            <button onClick={confirmClose} disabled={closing} style={{ ...S.btnPrimary, background:'#10b981' }}>{closing?'Closing...':'Confirm & close order'}</button>
            <button onClick={() => setShowCloseModal(false)} style={S.btnSecondary}>Cancel</button>
          </div>
        </div>
      </div>
    );
  };

  if (!data) return <div style={{ padding:40, textAlign:'center', color:'#9ca3af' }}>Loading...</div>;
  const { order, items, notes, shipments, documents, alerts } = data;
  const realItems = items.filter(i => i.size);
  const totalVal = items.reduce((s,i) => s + ((i.qty_ordered||0)*(i.price_per_unit||0)), 0);
  const totalShipped = items.reduce((s,i) => s + (i.qty_shipped||0), 0);
  const totalOrdered = items.reduce((s,i) => s + (i.qty_ordered||0), 0);

  // Editable field component — inline edit
  const EditableField = ({ label, field, type='text', options=null }) => {
    const isEditing = editingField === field;
    const value = editForm[field] || order[field] || '';
    return (
      <div>
        <div style={{ fontSize:11, color:'#9ca3af', marginBottom:2 }}>{label}</div>
        {isEditing ? (
          <div style={{ display:'flex', gap:4, alignItems:'center' }}>
            {options ? (
              <select value={editForm[field]||''} onChange={e => setEditForm(f=>({...f,[field]:e.target.value}))} style={{ ...S.input, fontSize:13, padding:'4px 8px' }}>
                {options.map(o => <option key={o}>{o}</option>)}
              </select>
            ) : type === 'date' ? (
              <input
                defaultValue={editForm[field] ? (() => {
                  const p = (editForm[field]||'').split('-');
                  if (p.length===3 && p[0].length===4) return `${p[2]}-${p[1]}-${p[0].slice(2)}`;
                  return editForm[field];
                })() : ''}
                onBlur={e => {
                  const v = e.target.value.trim();
                  const parts = v.split('-');
                  if (parts.length === 3) {
                    const [d, m, y] = parts;
                    const year = y.length === 2 ? `20${y}` : y;
                    setEditForm(f => ({...f, [field]: `${year}-${m}-${d}`}));
                  }
                }}
                placeholder="dd-mm-yy"
                style={{ ...S.input, fontSize:13, padding:'4px 8px', width:110 }}
              />
            ) : (
              <input value={editForm[field]||''} onChange={e => setEditForm(f=>({...f,[field]:e.target.value}))} style={{ ...S.input, fontSize:13, padding:'4px 8px' }} />
            )}
            <button onClick={saveHeader} style={{ ...S.btnSm, background:'#3b82f6', color:'#fff', padding:'4px 10px' }}>✓</button>
            <button onClick={() => setEditingField(null)} style={{ ...S.btnSm, background:'#f3f4f6', padding:'4px 8px' }}>✕</button>
          </div>
        ) : (
          <div style={{ display:'flex', alignItems:'center', gap:6 }}>
            <span style={{ fontSize:13, fontWeight:500 }}>{value || '—'}</span>
            <button onClick={() => setEditingField(field)} style={{ background:'none', border:'none', color:'#9ca3af', cursor:'pointer', fontSize:11, padding:0 }}>✎</button>
          </div>
        )}
      </div>
    );
  };

  return (
    <div>
      {/* Header */}
      <div style={{ display:'flex', alignItems:'center', gap:12, marginBottom:16 }}>
        <Link to="/orders" style={{ color:'#9ca3af', textDecoration:'none', fontSize:13 }}>← Orders</Link>
        <span style={{ color:'#d1d5db' }}>/</span>
        <span style={{ fontSize:18, fontWeight:600 }}>{order.pi_number}</span>
        <span style={{ background:order.status==='closed'?'#f0fdf4':'#eff6ff', color:order.status==='closed'?'#10b981':'#3b82f6', padding:'3px 10px', borderRadius:20, fontSize:12, fontWeight:500 }}>{order.status}</span>
      </div>

      {/* Alerts */}
      {alerts?.map((a,i) => (
        <div key={i} style={{ background:a.type==='overdue'||a.type==='urgent'?'#fef2f2':'#fffbeb', border:`1px solid ${a.type==='urgent'||a.type==='overdue'?'#fecaca':'#fde68a'}`, borderRadius:8, padding:'8px 14px', marginBottom:12, fontSize:13, color:a.type==='overdue'||a.type==='urgent'?'#dc2626':'#92400e', fontWeight:500 }}>
          {a.type==='overdue'?'🔴':a.type==='urgent'?'🟠':'🟡'} {a.message}
        </div>
      ))}

      {/* Order details — inline editable fields */}
      <div style={{ background:'#fff', border:'1px solid #e5e7eb', borderRadius:10, padding:'16px 20px', marginBottom:16 }}>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:14 }}>
          <div style={{ fontSize:13, fontWeight:600, color:'#374151' }}>Order details <span style={{ fontSize:11, color:'#9ca3af', fontWeight:400 }}>— click ✎ on any field to edit</span></div>
          <div style={{ display:'flex', gap:8 }}>
            <button onClick={downloadWorkOrder} style={{ ...S.btnSecondary, fontSize:12 }}>📄 Work Order</button>
            <button onClick={() => setShowDuplicate(true)} style={S.btnSecondary}>Duplicate</button>
            <button onClick={deleteOrder} style={{ ...S.btnSm, background:'#fef2f2', color:'#ef4444', padding:'6px 14px' }}>Delete order</button>
            {order.status==='active'
              ? <button onClick={closeOrder} style={{ ...S.btnSm, background:'#f0fdf4', color:'#10b981', padding:'6px 14px' }}>✓ Close order</button>
              : <button onClick={reopenOrder} style={{ ...S.btnSm, background:'#fffbeb', color:'#f59e0b', padding:'6px 14px' }}>Reopen</button>
            }
          </div>
        </div>
        <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:16 }}>
          <div><div style={{ fontSize:11, color:'#9ca3af', marginBottom:2 }}>Customer</div><div style={{ fontSize:13, fontWeight:500 }}>{order.company_name}</div></div>
          <EditableField label="Customer PO" field="customer_po" />
          <EditableField label="Expected ship date" field="expected_ship_date" type="date" />
          <EditableField label="Shipping terms" field="shipping_terms" options={['FOB','CIF','CFR','EXW','DAP']} />
          <EditableField label="Destination port" field="destination_port" />
          <EditableField label="Payment terms" field="payment_terms" />
          <EditableField label="HS Code" field="hs_code" />
          <div>
            <div style={{ fontSize:11, color:'#9ca3af', marginBottom:2 }}>Order value</div>
            <div style={{ fontSize:13, fontWeight:700, color:'#3b82f6' }}>{order.currency} {totalVal.toLocaleString(undefined,{minimumFractionDigits:2,maximumFractionDigits:2})}</div>
          </div>
        </div>
        <div style={{ marginTop:12 }}>
          <EditableField label="Special instructions" field="special_instructions" />
        </div>
        <div style={{ marginTop:8, fontSize:12, color:'#9ca3af' }}>
          Shipped: {totalShipped.toLocaleString()} / {totalOrdered.toLocaleString()} pcs
        </div>
      </div>

      {/* Tabs */}
      <div style={{ background:'#fff', border:'1px solid #e5e7eb', borderRadius:10, overflow:'hidden' }}>
        <div style={{ display:'flex', borderBottom:'1px solid #e5e7eb', overflowX:'auto' }}>
          <TabBtn label="Line items" active={tab==='items'} onClick={() => setTab('items')} />
          <TabBtn label="BOM" active={tab==='bom'} onClick={() => setTab('bom')} />
          <TabBtn label="Production" active={tab==='stages'} onClick={() => setTab('stages')} />
          {order.status !== 'closed' && <TabBtn label="Dispatch" active={tab==='dispatch'} onClick={() => setTab('dispatch')} badge={shipments.length||null} />}
          {order.status === 'closed' && shipments.length > 0 && <TabBtn label={`Dispatched (${shipments.length})`} active={tab==='dispatch'} onClick={() => setTab('dispatch')} />}
          <TabBtn label={`Notes (${notes.length})`} active={tab==='notes'} onClick={() => setTab('notes')} />
          <TabBtn label={`Docs (${documents.length})`} active={tab==='docs'} onClick={() => setTab('docs')} />
        </div>

        <div style={{ padding:20 }}>
          {/* LINE ITEMS */}
          {tab==='items' && (
            <LineItemsTab order={order} items={items} realItems={realItems} totalVal={totalVal} totalOrdered={totalOrdered} totalShipped={totalShipped} reload={load} />
          )}

          {tab==='bom' && realItems.length > 0 && <BOMTab order={order} items={realItems} />}
          {tab==='bom' && realItems.length === 0 && <div style={{ color:'#9ca3af', textAlign:'center', padding:40 }}>No line items</div>}
          {tab==='stages' && realItems.length > 0 && <StagesTab order={order} items={realItems} />}
          {tab==='stages' && realItems.length === 0 && <div style={{ color:'#9ca3af', textAlign:'center', padding:40 }}>No line items</div>}
          {tab==='dispatch' && <DispatchTab order={order} items={realItems} shipments={shipments} reload={load} />}

          {/* NOTES — always accessible */}
          {tab==='notes' && (
            <div>
              <div style={{ display:'flex', justifyContent:'space-between', marginBottom:16 }}>
                <div>
                  <div style={{ fontWeight:600, fontSize:15 }}>Order notes</div>
                  <div style={{ fontSize:12, color:'#9ca3af', marginTop:2 }}>Notes are always accessible regardless of order status</div>
                </div>
                <button onClick={() => setShowNote(true)} style={S.btnPrimary}>+ Add note</button>
              </div>
              {notes.length === 0 && <div style={{ color:'#9ca3af', textAlign:'center', padding:30 }}>No notes yet</div>}
              {notes.map(n => {
                const nc = NOTE_COLORS[n.category] || NOTE_COLORS.General;
                return (
                  <div key={n.id} style={{ border:'1px solid #e5e7eb', borderLeft:`4px solid ${nc.border}`, borderRadius:8, padding:14, marginBottom:10 }}>
                    <div style={{ display:'flex', justifyContent:'space-between', marginBottom:6 }}>
                      <span style={{ background:nc.bg, color:nc.color, padding:'2px 9px', borderRadius:20, fontSize:11, fontWeight:500 }}>{n.category}</span>
                      <div style={{ display:'flex', gap:10, alignItems:'center' }}>
                        <span style={{ fontSize:11, color:'#9ca3af' }}>{n.created_at?.split('T')[0]} · {n.user_name||'—'}</span>
                        <button onClick={() => deleteNote(n.id)} style={{ background:'none', border:'none', color:'#ef4444', cursor:'pointer', fontSize:12 }}>Delete</button>
                      </div>
                    </div>
                    <div style={{ fontSize:13, color:'#374151', lineHeight:1.6 }}>{n.note}</div>
                  </div>
                );
              })}
            </div>
          )}

          {/* DOCUMENTS */}
          {tab==='docs' && (
            <div>
              <div style={{ display:'flex', justifyContent:'space-between', marginBottom:16 }}>
                <div style={{ fontWeight:600, fontSize:15 }}>Documents</div>
                <div>
                  <input type="file" ref={fileRef} style={{ display:'none' }} onChange={e => { if(e.target.files[0]) uploadDoc(e.target.files[0]); }} />
                  <button onClick={() => fileRef.current?.click()} style={S.btnPrimary}>Upload document</button>
                </div>
              </div>
              {documents.length === 0 && <div style={{ color:'#9ca3af', textAlign:'center', padding:30 }}>No documents yet. Upload work orders, packing lists etc.</div>}
              {documents.map(d => (
                <div key={d.id} style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'10px 14px', background:'#f9fafb', border:'1px solid #e5e7eb', borderRadius:8, marginBottom:8 }}>
                  <div>
                    <div style={{ fontSize:13, fontWeight:500 }}>{d.original_name}</div>
                    <div style={{ fontSize:11, color:'#9ca3af' }}>{d.uploaded_at?.split('T')[0]} · {Math.round((d.file_size||0)/1024)} KB</div>
                  </div>
                  <div style={{ display:'flex', gap:8 }}>
                    <a href={`/api/orders/${id}/documents/${d.id}/download`} download={d.original_name} style={{ ...S.btnSm, background:'#eff6ff', color:'#3b82f6', textDecoration:'none' }}>Download</a>
                    <button onClick={() => deleteDoc(d.id)} style={{ ...S.btnSm, background:'#fef2f2', color:'#ef4444' }}>Delete</button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Add note modal */}
      {showNote && (
        <div style={S.overlay}>
          <div style={{ ...S.modal, width:480 }}>
            <div style={{ fontSize:15, fontWeight:600, marginBottom:16 }}>Add note</div>
            <div style={{ marginBottom:12 }}>
              <label style={S.label}>Category</label>
              <select value={noteForm.category} onChange={e => setNoteForm(f=>({...f,category:e.target.value}))} style={S.input}>
                {NOTE_CATS.map(c => <option key={c}>{c}</option>)}
              </select>
            </div>
            <div style={{ marginBottom:16 }}><label style={S.label}>Note *</label><textarea value={noteForm.note} onChange={e => setNoteForm(f=>({...f,note:e.target.value}))} style={{ ...S.input, height:100, resize:'vertical' }} placeholder="Enter note..." /></div>
            <div style={{ display:'flex', gap:10 }}>
              <button onClick={addNote} disabled={!noteForm.note.trim()} style={S.btnPrimary}>Save note</button>
              <button onClick={() => setShowNote(false)} style={S.btnSecondary}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* Duplicate modal */}
      {showCloseModal && <CloseOrderModal />}
      {showDuplicate && (
        <div style={S.overlay}>
          <div style={{ ...S.modal, width:400 }}>
            <div style={{ fontSize:15, fontWeight:600, marginBottom:4 }}>Duplicate order</div>
            <div style={{ fontSize:13, color:'#6b7280', marginBottom:16 }}>Creates a new order with same line items, customer and terms.</div>
            <div style={{ marginBottom:16 }}><label style={S.label}>New PI number *</label><input value={newPi} onChange={e => setNewPi(e.target.value)} style={S.input} placeholder="e.g. PI-2520" /></div>
            <div style={{ display:'flex', gap:10 }}>
              <button onClick={duplicate} disabled={!newPi.trim()} style={S.btnPrimary}>Duplicate</button>
              <button onClick={() => { setShowDuplicate(false); setNewPi(''); }} style={S.btnSecondary}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
